import { useEffect, useState } from "react";
import { api, buildKickLoginUrl, buildLoginUrl } from "../lib/api";
import { Activity, AlertTriangle, Download, KeyRound, Radio, Save, Shield, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Settings() {
  const { me, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [excludedText, setExcludedText] = useState("");
  const [logs, setLogs] = useState([]);
  const [backupExported, setBackupExported] = useState(false);
  const [resetText, setResetText] = useState("");
  const [twitchHealth, setTwitchHealth] = useState(null);
  const [twitchRegistering, setTwitchRegistering] = useState(false);
  const [kickRegistering, setKickRegistering] = useState(false);
  const [liveToggleBusy, setLiveToggleBusy] = useState(false);

  const loadTwitchHealth = async () => {
    try {
      const { data } = await api.get("/devops/health");
      setTwitchHealth(data);
    } catch (e) {
      setTwitchHealth(null);
    }
  };

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setSettings(data);
      setExcludedText((data.excluded_users || []).map((u) => u.login).join("\n"));
    });
    api.get("/logs?limit=25").then(({ data }) => setLogs(data)).catch(() => {});
    loadTwitchHealth();
  }, []);

  const save = async () => {
    const excluded_users = excludedText
      .split(/\r?\n|,/)
      .map((login) => login.trim().replace(/^@/, "").toLowerCase())
      .filter(Boolean)
      .map((login) => ({ login }));
    await api.patch("/settings", {
      command_prefix: settings.command_prefix,
      watchtime_interval_seconds: settings.watchtime_interval_seconds,
      excluded_users,
      secret_word: settings.secret_word,
      gamble: settings.gamble,
    });
    toast.success("Settings saved");
    refresh();
  };

  const exportBackup = async () => {
    try {
      const response = await api.get("/admin/backup/export", { responseType: "blob" });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] || "thebarracksbunny-backup.json";
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      setBackupExported(true);
      toast.success("Backup exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    }
  };

  const resetCommunityData = async () => {
    try {
      await api.post("/admin/dangerous/reset-community-data", {
        confirmation: resetText,
        backup_exported: backupExported,
      });
      setResetText("");
      setBackupExported(false);
      const { data } = await api.get("/logs?limit=25");
      setLogs(data);
      toast.success("Community data reset");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset blocked");
    }
  };

  const registerTwitchEvents = async () => {
    setTwitchRegistering(true);
    try {
      const { data } = await api.post("/admin/twitch/eventsub/register");
      const created = Number(data.created_count || 0);
      const present = Number(data.already_present_count || 0);
      const failed = Number(data.failed_count || 0);
      const skipped = Number(data.skipped_count || 0);
      const reason = data.results?.find((item) => item.reason)?.reason;
      if (failed > 0 || data.status === "failed" || data.status === "partial_failure") {
        toast.error(reason || "Twitch EventSub registration failed");
      } else if (skipped > 0 || data.status === "skipped") {
        toast.warning(reason || "Twitch EventSub registration skipped");
      } else {
        toast.success(`Twitch events ready (${created} created, ${present} already linked)`);
      }
      await loadTwitchHealth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Twitch EventSub registration failed");
    } finally {
      setTwitchRegistering(false);
    }
  };

  const registerKickEvents = async () => {
    setKickRegistering(true);
    try {
      const { data } = await api.post("/admin/kick/events/register");
      const created = Number(data.created_count || 0);
      const present = Number(data.already_present_count || 0);
      const failed = Number(data.failed_count || 0);
      const skipped = Number(data.skipped_count || 0);
      const reason = data.results?.find((item) => item.reason)?.reason;
      if (failed > 0 || data.status === "failed" || data.status === "partial_failure") {
        toast.error(reason || "Kick event registration failed");
      } else if (skipped > 0 || data.status === "skipped") {
        toast.warning(reason || "Kick event registration skipped");
      } else {
        toast.success(`Kick events ready (${created} created, ${present} already linked)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kick event registration failed");
    } finally {
      setKickRegistering(false);
    }
  };

  const setManualLiveState = async (stream_is_live) => {
    setLiveToggleBusy(true);
    try {
      const { data } = await api.patch("/settings", { stream_is_live });
      setSettings(data);
      refresh();
      toast.success(stream_is_live ? "Stream marked live." : "Stream marked offline.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update stream state.");
    } finally {
      setLiveToggleBusy(false);
    }
  };

  if (!settings) return <div className="text-sm">Loading...</div>;
  const twitchSubscriptionCount = Number(twitchHealth?.eventsub_existing_subscriptions?.subscription_count || 0);
  const twitchWebhookDeliveries = Number(twitchHealth?.eventsub_delivery_diagnostics?.webhook_delivery_log_count || 0);
  const twitchWebhookWarnings = Number(twitchHealth?.eventsub_delivery_diagnostics?.webhook_warning_count || 0);
  const twitchEventSubReady = twitchSubscriptionCount > 0 && twitchWebhookWarnings === 0;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="label-eyebrow">Configuration</div>
        <h1 className="text-2xl font-semibold tracking-tight mt-1">Settings</h1>
      </div>

      <div className="surface p-5 space-y-3">
        <div className="label-eyebrow">Connected accounts</div>
        <Row label="Streamer">
          <div className="flex items-center gap-3">
            {me?.streamer?.profile_image_url && <img alt="" src={me.streamer.profile_image_url} className="w-8 h-8 rounded-full" />}
            <span className="font-mono">{me?.streamer?.display_name || "-"}</span>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">#{me?.streamer?.twitch_login}</span>
          </div>
        </Row>
        <Row label="Twitch EventSub">
          <div className="flex flex-wrap items-center justify-end gap-3 text-sm">
            <span className={`text-xs uppercase tracking-widest px-2.5 py-1.5 rounded border ${twitchEventSubReady ? "border-[hsl(var(--accent-live))] text-[hsl(var(--accent-live))]" : "border-[hsl(var(--destructive))]/60 text-[hsl(var(--destructive))]"}`}>
              {twitchSubscriptionCount} registered
            </span>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">{twitchWebhookDeliveries} webhook checks</span>
            <button
              type="button"
              onClick={registerTwitchEvents}
              disabled={twitchRegistering}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[hsl(var(--primary))] text-[hsl(var(--primary))] px-3 py-2 rounded disabled:opacity-50"
              data-testid="register-twitch-eventsub"
            >
              <Activity className="w-3 h-3" /> {twitchRegistering ? "Registering" : "Register events"}
            </button>
            <button
              type="button"
              onClick={loadTwitchHealth}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] px-3 py-2 rounded hover:text-white"
            >
              Refresh
            </button>
          </div>
        </Row>
        <Row label="Bot account">
          {me?.bot ? (
            <div className="flex items-center gap-3">
              {me.bot.profile_image_url && <img alt="" src={me.bot.profile_image_url} className="w-8 h-8 rounded-full" />}
              <span className="font-mono">{me.bot.display_name}</span>
              <a href={buildLoginUrl("bot")} target="_top" rel="noopener" className="ml-3 text-xs underline text-[hsl(var(--primary))]" data-testid="reconnect-bot">re-auth</a>
            </div>
          ) : (
            <a href={buildLoginUrl("bot")} target="_top" rel="noopener" data-testid="connect-bot-btn-settings" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[hsl(var(--primary))] text-[hsl(var(--primary))] px-3 py-2 rounded">
              <Radio className="w-3 h-3" /> Connect Bot Account
            </a>
          )}
        </Row>
        <Row label="Kick bot">
          {me?.kick_bot ? (
            <div className="flex flex-wrap items-center gap-3">
              {me.kick_bot.profile_image_url && <img alt="" src={me.kick_bot.profile_image_url} className="w-8 h-8 rounded-full" />}
              <span className="font-mono">{me.kick_bot.display_name}</span>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">@{me.kick_bot.kick_login}</span>
              <a href={buildKickLoginUrl("bot", "/app/settings")} target="_top" rel="noopener" className="ml-3 text-xs underline text-[hsl(var(--primary))]" data-testid="reconnect-kick-bot">re-auth</a>
              <button
                type="button"
                onClick={registerKickEvents}
                disabled={kickRegistering}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[hsl(var(--primary))] text-[hsl(var(--primary))] px-3 py-2 rounded disabled:opacity-50"
                data-testid="register-kick-events"
              >
                <Radio className="w-3 h-3" /> {kickRegistering ? "Registering" : "Register events"}
              </button>
            </div>
          ) : (
            <a href={buildKickLoginUrl("bot", "/app/settings")} target="_top" rel="noopener" data-testid="connect-kick-bot-btn-settings" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[hsl(var(--primary))] text-[hsl(var(--primary))] px-3 py-2 rounded">
              <Radio className="w-3 h-3" /> Connect Kick Bot
            </a>
          )}
        </Row>
        <Row label="Channel">
          <span className="font-mono">#{me?.channel_login || settings.channel_login || "-"}</span>
        </Row>
      </div>

      <div className="surface p-5 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="label-eyebrow">Stream state override</div>
            <h2 className="text-lg font-semibold mt-1 flex items-center gap-2">
              <Radio className="w-4 h-4 text-[hsl(var(--primary))]" /> Manual live toggle
            </h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
              Use this only if Twitch live/offline detection is wrong. Chat rewards and live-only systems follow this state.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`text-xs uppercase tracking-widest px-3 py-2 rounded border ${settings.stream_is_live ? "border-[hsl(var(--accent-live))] text-[hsl(var(--accent-live))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
              {settings.stream_is_live ? "Currently live" : "Currently offline"}
            </span>
            <button
              type="button"
              onClick={() => setManualLiveState(!settings.stream_is_live)}
              disabled={liveToggleBusy}
              className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest px-4 py-2 rounded border disabled:opacity-50 ${
                settings.stream_is_live
                  ? "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"
                  : "border-[hsl(var(--accent-live))] text-[hsl(var(--accent-live))]"
              }`}
              data-testid="manual-live-toggle"
            >
              <Radio className="w-3 h-3" /> {liveToggleBusy ? "Updating" : settings.stream_is_live ? "Mark offline" : "Mark live"}
            </button>
          </div>
        </div>
      </div>

      <div className="surface p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Command prefix" value={settings.command_prefix} onChange={(v) => setSettings({ ...settings, command_prefix: v })} test="command-prefix" mono />
        <Field label="Active watchtime interval (seconds)" type="number" value={settings.watchtime_interval_seconds} onChange={(v) => setSettings({ ...settings, watchtime_interval_seconds: parseInt(v || 0, 10) })} test="watchtime-interval" />
        <div className="md:col-span-2">
          <button onClick={save} className="bg-[hsl(var(--primary))] text-white text-sm px-4 py-2 rounded flex items-center gap-2" data-testid="save-settings">
            <Save className="w-4 h-4" /> Save settings
          </button>
        </div>
      </div>

      <div className="surface p-5 space-y-4">
        <div>
          <div className="label-eyebrow">Live Reward</div>
          <h2 className="text-lg font-semibold mt-1 flex items-center gap-2"><KeyRound className="w-4 h-4 text-[hsl(var(--primary))]" /> Secret word</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Case-sensitive. Each viewer can claim once for the current saved word.</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(settings.secret_word?.enabled)}
            onChange={(e) => setSettings({ ...settings, secret_word: { ...(settings.secret_word || {}), enabled: e.target.checked } })}
            data-testid="secret-word-enabled"
          />
          Enabled for live chat
        </label>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Field
            label="Secret word"
            value={settings.secret_word?.word || ""}
            onChange={(word) => setSettings({ ...settings, secret_word: { ...(settings.secret_word || {}), word } })}
            test="secret-word-input"
            mono
          />
          <Field
            label="Bunny min"
            type="number"
            value={settings.secret_word?.free_min ?? 1}
            onChange={(free_min) => setSettings({ ...settings, secret_word: { ...(settings.secret_word || {}), free_min: parseInt(free_min || 0, 10) } })}
            test="secret-word-free-min"
          />
          <Field
            label="Bunny max"
            type="number"
            value={settings.secret_word?.free_max ?? 5}
            onChange={(free_max) => setSettings({ ...settings, secret_word: { ...(settings.secret_word || {}), free_max: parseInt(free_max || 0, 10) } })}
            test="secret-word-free-max"
          />
          <Field
            label="Rat min"
            type="number"
            value={settings.secret_word?.paid_min ?? 1}
            onChange={(paid_min) => setSettings({ ...settings, secret_word: { ...(settings.secret_word || {}), paid_min: parseInt(paid_min || 0, 10) } })}
            test="secret-word-paid-min"
          />
          <Field
            label="Rat max"
            type="number"
            value={settings.secret_word?.paid_max ?? 3}
            onChange={(paid_max) => setSettings({ ...settings, secret_word: { ...(settings.secret_word || {}), paid_max: parseInt(paid_max || 0, 10) } })}
            test="secret-word-paid-max"
          />
        </div>
        <button onClick={save} className="bg-[hsl(var(--primary))] text-white text-sm px-4 py-2 rounded flex items-center gap-2" data-testid="save-secret-word">
          <Save className="w-4 h-4" /> Save secret word
        </button>
      </div>

      <div className="surface p-5 space-y-4">
        <div>
          <div className="label-eyebrow">Chat Minigame</div>
          <h2 className="text-lg font-semibold mt-1 flex items-center gap-2"><Activity className="w-4 h-4 text-[hsl(var(--primary))]" /> Gamble</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Bunny Tokens only. Chat command: !gamble &lt;amount&gt; or !gamble all. Default is off until you enable it.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(settings.gamble?.enabled)}
            onChange={(e) => setSettings({ ...settings, gamble: { ...(settings.gamble || {}), enabled: e.target.checked } })}
            data-testid="gamble-enabled"
          />
          Enabled for chat
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={settings.gamble?.live_only !== false}
            onChange={(e) => setSettings({ ...settings, gamble: { ...(settings.gamble || {}), live_only: e.target.checked } })}
            data-testid="gamble-live-only"
          />
          Live stream only
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={settings.gamble?.allow_all !== false}
            onChange={(e) => setSettings({ ...settings, gamble: { ...(settings.gamble || {}), allow_all: e.target.checked } })}
            data-testid="gamble-allow-all"
          />
          Allow !gamble all (capped at max bet)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field
            label="Win chance %"
            type="number"
            value={settings.gamble?.win_chance_percent ?? 45}
            onChange={(win_chance_percent) => setSettings({ ...settings, gamble: { ...(settings.gamble || {}), win_chance_percent: parseInt(win_chance_percent || 0, 10) } })}
            test="gamble-win-chance"
          />
          <Field
            label="Payout multiplier"
            type="number"
            value={settings.gamble?.payout_multiplier ?? 2}
            onChange={(payout_multiplier) => setSettings({ ...settings, gamble: { ...(settings.gamble || {}), payout_multiplier: parseInt(payout_multiplier || 0, 10) } })}
            test="gamble-payout-multiplier"
          />
          <Field
            label="Viewer cooldown (seconds)"
            type="number"
            value={settings.gamble?.viewer_cooldown_seconds ?? 30}
            onChange={(viewer_cooldown_seconds) => setSettings({ ...settings, gamble: { ...(settings.gamble || {}), viewer_cooldown_seconds: parseInt(viewer_cooldown_seconds || 0, 10) } })}
            test="gamble-cooldown"
          />
          <Field
            label="Min bet"
            type="number"
            value={settings.gamble?.min_bet ?? 5}
            onChange={(min_bet) => setSettings({ ...settings, gamble: { ...(settings.gamble || {}), min_bet: parseInt(min_bet || 0, 10) } })}
            test="gamble-min-bet"
          />
          <Field
            label="Max bet"
            type="number"
            value={settings.gamble?.max_bet ?? 500}
            onChange={(max_bet) => setSettings({ ...settings, gamble: { ...(settings.gamble || {}), max_bet: parseInt(max_bet || 0, 10) } })}
            test="gamble-max-bet"
          />
          <Field
            label="Big win threshold"
            type="number"
            value={settings.gamble?.big_win_threshold ?? 250}
            onChange={(big_win_threshold) => setSettings({ ...settings, gamble: { ...(settings.gamble || {}), big_win_threshold: parseInt(big_win_threshold || 0, 10) } })}
            test="gamble-big-win"
          />
        </div>
        <button onClick={save} className="bg-[hsl(var(--primary))] text-white text-sm px-4 py-2 rounded flex items-center gap-2" data-testid="save-gamble">
          <Save className="w-4 h-4" /> Save gamble settings
        </button>
      </div>

      <div className="surface p-5 space-y-2" data-testid="coworking-settings-pointer">
        <div className="label-eyebrow">Coworking Suite</div>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          Configure focus timer, tasks, and OBS overlays in{" "}
          <a href="/app/coworking" className="text-[hsl(var(--primary))] underline" data-testid="coworking-admin-link">
            Admin → Coworking
          </a>
          .
        </p>
      </div>

      <div className="surface p-5 space-y-4">
        <div>
          <div className="label-eyebrow">Safety</div>
          <h2 className="text-lg font-semibold mt-1 flex items-center gap-2"><Shield className="w-4 h-4 text-[hsl(var(--primary))]" /> Excluded users</h2>
        </div>
        <textarea
          value={excludedText}
          onChange={(e) => setExcludedText(e.target.value)}
          rows={6}
          data-testid="excluded-users"
          className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm font-mono"
          placeholder={"streamlabs\nnightbot\nstreamelements"}
        />
        <button onClick={save} className="bg-[hsl(var(--primary))] text-white text-sm px-4 py-2 rounded flex items-center gap-2" data-testid="save-excluded-users">
          <Save className="w-4 h-4" /> Save excluded users
        </button>
      </div>

      <details className="surface p-5 group">
        <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
          <span>
            <span className="label-eyebrow">Advanced</span>
            <span className="text-lg font-semibold mt-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[hsl(var(--destructive))]" /> Dangerous Actions</span>
          </span>
          <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] group-open:hidden">Open</span>
        </summary>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="surface-2 p-4 space-y-3">
            <div className="label-eyebrow">Backup</div>
            <button onClick={exportBackup} className="border border-[hsl(var(--border))] text-sm px-4 py-2 rounded flex items-center gap-2" data-testid="export-backup">
              <Download className="w-4 h-4" /> Export backup
            </button>
          </div>

          <div className="surface-2 p-4 space-y-3">
            <div className="label-eyebrow">Reset</div>
            <input
              value={resetText}
              onChange={(e) => setResetText(e.target.value)}
              data-testid="reset-confirmation"
              className="w-full bg-[hsl(var(--surface-1))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm font-mono"
              placeholder="RESET ALL DATA"
            />
            <button
              onClick={resetCommunityData}
              disabled={resetText !== "RESET ALL DATA" || !backupExported}
              className="bg-[hsl(var(--destructive))] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm px-4 py-2 rounded flex items-center gap-2"
              data-testid="reset-community-data"
            >
              <Trash2 className="w-4 h-4" /> Reset community data
            </button>
          </div>
        </div>

        <div className="mt-5 surface-2">
          <div className="px-4 py-3 border-b border-[hsl(var(--border))] label-eyebrow">Event and error logs</div>
          <ul>
            {logs.length === 0 && <li className="px-4 py-6 text-sm text-[hsl(var(--muted-foreground))]">No logs yet.</li>}
            {logs.map((log) => (
              <li key={log.id} className="px-4 py-3 border-t first:border-t-0 border-[hsl(var(--border))]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-xs uppercase tracking-widest ${log.level === "error" || log.level === "warning" ? "text-[hsl(var(--destructive))]" : "text-[hsl(var(--primary))]"}`}>{log.level}</span>
                  <span className="text-xs font-mono text-[hsl(var(--muted-foreground))]">{log.category}</span>
                  <span className="text-xs font-mono text-[hsl(var(--muted-foreground))]">{new Date(log.at).toLocaleString()}</span>
                </div>
                <div className="text-sm mt-1">{log.message}</div>
              </li>
            ))}
          </ul>
        </div>
      </details>
    </div>
  );
}

function Row({ label = "", children } = {}) {
  return (
    <div className="flex items-center justify-between border-t first:border-t-0 border-[hsl(var(--border))] py-3">
      <span className="label-eyebrow">{label}</span>
      {children}
    </div>
  );
}

function Field({ label = "", value, onChange, type = "text", test, mono } = {}) {
  return (
    <div>
      <div className="label-eyebrow mb-1.5">{label}</div>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={test} className={`w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm ${mono ? "font-mono" : ""}`} />
    </div>
  );
}
